import "./sentry";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import { COMMAND_STYLES, applyStyleColors, type CommandStyleId } from "./lib/command-styles";

// Apply saved colors BEFORE React renders — prevents flash of wrong theme
(function applyStoredTheme() {
  const commandStyle = localStorage.getItem("ho-command-style") as CommandStyleId | null;

  if (commandStyle && COMMAND_STYLES[commandStyle]) {
    // Apply command style colors synchronously
    applyStyleColors(COMMAND_STYLES[commandStyle].colors);
    return;
  }

  // Fallback: apply brand kit overrides if no command style
  function hexToHsl(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  }

  const brandKeys: Array<{ key: string; cssVar: string; cssVarFg?: string }> = [
    { key: "ho-accent-color", cssVar: "--primary", cssVarFg: "--primary-foreground" },
    { key: "ho-brand-bg", cssVar: "--background" },
    { key: "ho-brand-card", cssVar: "--card" },
    { key: "ho-brand-sidebar", cssVar: "--sidebar-background" },
    { key: "ho-brand-border", cssVar: "--border" },
  ];

  for (const { key, cssVar, cssVarFg } of brandKeys) {
    const hex = localStorage.getItem(key);
    if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) continue;
    document.documentElement.style.setProperty(cssVar, hexToHsl(hex));
    if (cssVarFg) {
      const luma = 0.299 * parseInt(hex.slice(1, 3), 16) + 0.587 * parseInt(hex.slice(3, 5), 16) + 0.114 * parseInt(hex.slice(5, 7), 16);
      document.documentElement.style.setProperty(cssVarFg, luma > 128 ? "0 0% 10%" : "0 0% 98%");
    }
  }
})();

// Force SW update check on load and every time app comes to foreground.
// Prevents mobile PWA from getting stuck serving stale cached assets.
//
// Registration is done manually here (vite.config.ts uses injectRegister:false)
// so we can attach a .catch() — the auto-injected /registerSW.js had a bare
// `navigator.serviceWorker.register(...)` call with no catch, which surfaced
// every transient browser/network rejection as an unhandled promise rejection.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch((err) => {
      console.warn("Service worker registration failed:", err);
    });
  });

  // ready resolves once a worker is active; if registration failed on a
  // first-ever visit it just never resolves (no rejection fires, so this
  // is not a Sentry risk — the stale-cache update path simply stays dormant
  // until the next successful registration).
  navigator.serviceWorker.ready
    .then((registration) => {
      registration.update();
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          registration.update();
        }
      });
    })
    .catch(() => {
      /* registration failed earlier; nothing to update */
    });
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
